/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";

/**
 * In-home location data held by the post-sign-in startup sequence and
 * refreshed on every network-graph settlement that confirms internet
 * connectivity.
 *
 * Null fields indicate the last fetch either has not yet completed or failed.
 */
export interface TInHomeLocationRecord {
  inHome: boolean | null;
  inRegion: boolean | null;
  inBlacklist: boolean | null;
}

/**
 * Model-layer accessor for the in-home location status.
 *
 * Stores the last-known in-home/in-region/in-blacklist flags in a private
 * static field so that model-layer consumers can
 * read them synchronously without depending on Redux state, which is not
 * accessible from the model layer.
 *
 * In-memory storage is the approved approach for this data because:
 * - The value is refreshed on every startup and on every network-graph
 *   settlement — a relaunch always re-fetches, so persistence across
 *   sessions is not required.
 * - The data is non-sensitive and read-heavy (many consumers, few writes).
 * - Synchronous access avoids async complexity at model-layer call sites.
 *
 * The model is updated by the post-sign-in startup sequence and on every network-graph settlement that confirms internet connectivity.
 */
export class InHomeLocationModel {
  private static readonly TAG: string = "InHomeLocationModel";

  /** In-memory store. Reset to null when the app/module is initialised. */
  private static mRecord: TInHomeLocationRecord | null = null;

  /**
   * Store the in-home location status in memory.
   *
   * @param data - The in-home location flags to store
   */
  public static set(data: TInHomeLocationRecord): void {
    InHomeLocationModel.mRecord = data;
    Log.d(InHomeLocationModel.TAG, "set() stored in-home location");
  }

  /**
   * Read the last-known in-home location status.
   *
   * Returns null if {@link set} has not been called yet (e.g. before the
   * first successful startup fetch, or after a page reload).
   */
  public static get(): TInHomeLocationRecord | null {
    return InHomeLocationModel.mRecord;
  }
}
